import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { BookOpen, Plus, Search, CheckCircle2, Circle, Filter } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export default function TopicsList() {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTopics();
  }, []);

  useEffect(() => {
    let filtered = topics;
    
    if (selectedSpecialty !== 'all') {
      filtered = filtered.filter(t => t.specialty === selectedSpecialty);
    }
    
    if (searchQuery.trim()) {
      filtered = filtered.filter(topic => 
        topic.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredTopics(filtered);
  }, [searchQuery, selectedSpecialty, topics]);

  const getUniqueSpecialties = () => {
    const specialties = [...new Set(topics.filter(t => t.specialty).map(t => t.specialty))];
    return specialties.sort();
  };

  const fetchTopics = async () => {
    try {
      const response = await api.get('/topics');
      setTopics(response.data);
      setFilteredTopics(response.data);
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast.error('Erro ao carregar temas');
    } finally {
      setLoading(false);
    }
  };

  const getCompletionStatus = (topic) => {
    const completed = [topic.reading_done, topic.deep_dive_done, topic.questions_done].filter(Boolean).length;
    return { completed, total: 3 };
  };

  const isTopicComplete = (topic) => {
    return topic.reading_done && topic.deep_dive_done && topic.questions_done;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="topics-list-page">
      <div className="max-w-md mx-auto md:max-w-3xl px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-4xl font-bold tracking-tight">Meus Temas</h1>
            <Button onClick={() => navigate('/topics/new')} data-testid="add-topic-fab">
              <Plus className="w-5 h-5 mr-2" />
              Novo
            </Button>
          </div>
          <p className="text-lg text-muted-foreground mb-6">Todos os seus temas de estudo</p>
          
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar temas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
              <SelectTrigger className="w-48" data-testid="specialty-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Especialidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {getUniqueSpecialties().map(spec => (
                  <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <p className="text-sm text-muted-foreground">
            Mostrando {filteredTopics.length} de {topics.length} temas
          </p>
        </div>

        {filteredTopics.length === 0 ? (
          <Card className="shadow-sm">
            <CardContent className="text-center py-12">
              {searchQuery ? (
                <>
                  <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Nenhum tema encontrado</h3>
                  <p className="text-muted-foreground mb-6">
                    Tente buscar com outros termos
                  </p>
                </>
              ) : (
                <>
                  <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Nenhum tema cadastrado</h3>
                  <p className="text-muted-foreground mb-6">
                    Comece adicionando seu primeiro tema de estudo
                  </p>
                  <Button onClick={() => navigate('/topics/new')} data-testid="add-first-topic-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Tema
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredTopics.map((topic) => {
              const status = getCompletionStatus(topic);
              const isComplete = isTopicComplete(topic);
              
              return (
                <Card 
                  key={topic.id} 
                  className="shadow-sm hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate(`/topics/${topic.id}`)}
                  data-testid={`topic-item-${topic.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-2">{topic.title}</h3>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                            {topic.specialty || 'Outros'}
                          </span>
                          <span>•</span>
                          <span>{format(parseISO(topic.scheduled_date), "dd/MM/yyyy", { locale: ptBR })}</span>
                        </div>
                      </div>
                      {isComplete ? (
                        <CheckCircle2 className="w-6 h-6 text-green-600" data-testid={`complete-icon-${topic.id}`} />
                      ) : (
                        <Circle className="w-6 h-6 text-slate-400" data-testid={`incomplete-icon-${topic.id}`} />
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <Badge variant={topic.reading_done ? "default" : "outline"} className="text-xs">
                          Leitura
                        </Badge>
                        <Badge variant={topic.deep_dive_done ? "default" : "outline"} className="text-xs">
                          Aprofundamento
                        </Badge>
                        <Badge variant={topic.questions_done ? "default" : "outline"} className="text-xs">
                          Questões
                        </Badge>
                      </div>
                    </div>

                    {topic.questions_total > 0 && (
                      <div className="mt-4 pt-4 border-t flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Taxa de acerto</span>
                        <span className="text-lg font-bold text-brand" data-testid={`accuracy-badge-${topic.id}`}>
                          {topic.correct_rate}%
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
